// Globaler Fetch-Interceptor für Device-Limit-Enforcement (Task #25).
//
// Wenn das Backend einen 401 für /api/*-Calls liefert mit `reason:
// 'session_replaced'` oder `'session_not_registered'`, wurde die
// Session entweder durch einen Login auf einem anderen Gerät
// verdrängt oder ist nie registriert worden. `'session_expired'`
// heißt: 7 Tage ohne Nutzung, Server hat die Sitzung beendet (H22).
// Alle Fälle bedeuten: dieser Browser-Tab muss raus.
//
// Statt jeden einzelnen fetch-Aufrufer (>15 Stellen) anzufassen,
// patchen wir window.fetch einmal beim App-Boot. Nicht-/api-Calls und
// 401s anderer Gründe (z.B. abgelaufener Token vor record-login)
// bleiben unangetastet, damit das bestehende verifyToken-Verhalten
// nicht überrascht wird.

import { useAuthStore } from '../stores/authStore';
import { supabase } from './supabase';
import { toast, setErrorSuppressor } from './toast';
import { authFetch } from './authFetch';

let installed = false;

/**
 * Patch window.fetch um globalen 401-Handler. Idempotent: doppelter
 * Aufruf wird ignoriert.
 */
export function installFetchInterceptor() {
  if (installed) return;
  installed = true;

  // Während einer Eviction unterdrücken wir alle `error`-Toasts, weil
  // sonst sowohl der zentrale „Du wurdest abgemeldet"-Toast als auch
  // jeder einzelne Caller-Toast (z.B. SubBar handleFreeBerechnen
  // catch → toast.error(err.message)) gleichzeitig erscheinen. Nach
  // Ablauf des Eviction-Fensters geht Toast-Verhalten wieder normal.
  setErrorSuppressor(() => evictionInFlight);

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);

    // Nur /api/-Calls bekommen den Auto-Logout-Pfad. Statische Assets,
    // Supabase-Endpunkte, externe Calls bleiben unangetastet.
    const url = typeof input === 'string' ? input : input?.url;
    if (response.status !== 401 || !url || !url.includes('/api/')) {
      return response;
    }

    // Body-Parsing defensiv: nicht jeder 401 hat einen JSON-Body.
    // Wir clone() den Response, damit der Aufrufer weiter mit der
    // Original-Response arbeiten kann (Body-Stream ist „once-only").
    let reason = null;
    try {
      const cloned = response.clone();
      const data = await cloned.json();
      reason = data?.reason ?? null;
    } catch {
      // kein JSON-Body → nichts zu tun, gib den 401 unverändert weiter
    }

    if (reason === 'session_replaced' || reason === 'session_not_registered' || reason === 'session_expired') {
      handleSessionEvicted(reason);
    }

    return response;
  };
}

// Mehrere parallele /api/-Calls können kurz nacheinander alle in das
// 401-Fenster fallen (z.B. fetchProfile + fetchRemainingCalculations
// gleichzeitig). Ohne Debounce würde jeder davon einen eigenen Toast
// und einen eigenen signOut-Call auslösen — der User sieht doppelte
// Badges, das Server-Log bekommt überflüssige getUser-Calls mit
// bereits invalidiertem Token. Flag zurücksetzen nach 10 s damit ein
// späterer realer Wechsel (gleicher Tab, neuer Login → Eviction)
// wieder durchkommt.
let evictionInFlight = false;

const EVICTION_TEXT = {
  // H22: Server hat die Sitzung nach 7 Tagen ohne Lebenszeichen beendet.
  session_expired: 'Du wurdest abgemeldet, weil die App 7 Tage nicht genutzt wurde. Bitte melde dich neu an.',
  default: 'Du wurdest abgemeldet - die Sitzung wurde auf einem anderen Gerät ersetzt. (Pro: max. 2 Geräte gleichzeitig)'
};

function handleSessionEvicted(reason) {
  if (evictionInFlight) return;
  evictionInFlight = true;
  setTimeout(() => { evictionInFlight = false; }, 10000);

  // signOut() im authStore räumt lokalen State sofort, AuthGuard
  // redirected dann zur Login-Page. Supabase-SDK signOut läuft
  // best-effort daneben.
  const signOut = useAuthStore.getState().signOut;
  if (typeof signOut === 'function') {
    signOut().catch(() => {});
  }
  toast.error(
    EVICTION_TEXT[reason] ?? EVICTION_TEXT.default,
    // bypass damit unser eigener Eviction-Toast nicht von unserem
    // eigenen Suppressor unterdrückt wird. Caller-Toasts (z.B. SubBar
    // „Ungültiger Token") haben den Flag nicht und werden weiter gefiltert.
    { duration: 8000, bypassSuppressor: true }
  );
}

/**
 * Wird vom authStore nach jedem `SIGNED_IN`-Event aufgerufen.
 * Registriert die frische Session beim Server, damit die
 * Top-N-Cleanup-Logik greift und nachfolgende API-Calls den
 * Device-Limit-Check passieren.
 *
 * signIn() wartet selbst darauf, bevor es zurückkehrt; nach anderen
 * Auth-Ereignissen stößt der Listener es nur an (siehe authStore,
 * Listener darf nie auf Supabase warten). So schlüpft nach dem Login
 * keine API-Anfrage vor record-login durch (spurioser 401).
 */
export async function recordLoginSession(session) {
  if (!session?.access_token) return;
  try {
    await fetch('/api/auth/record-login', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}` }
    });
  } catch (err) {
    console.warn('[apiClient] record-login failed:', err.message);
  }
}

// Periodischer Heartbeat: löst Auto-Logout aus, sobald die Session
// auf einem anderen Gerät verdrängt wurde, auch wenn der User in
// diesem Tab keinen aktiven API-Call mehr macht (typischer Pro-Fall:
// Live-Calc rechnet rein client-seitig und triggert kein /api/).
//
// 60 s ist der Kompromiss aus "User merkt es zeitnah" und "Server-
// Last bleibt klein". Nur wenn der Tab sichtbar ist — versteckte Tabs
// pollen nicht, das spart Quota und ist datenschutzfreundlich. Bei
// 401 greift der bestehende fetch-Interceptor und macht den Eviction-
// Pfad. Returnt eine cancel-Funktion, falls jemand sie irgendwann
// gezielt stoppen will.
const HEARTBEAT_INTERVAL_MS = 60_000;

export function startHeartbeat() {
  const ping = async () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (!useAuthStore.getState().session) return;
    try {
      // authFetch holt einen frischen Token; nach langer Pause im
      // Hintergrund wäre der aus dem Store abgelaufen.
      await authFetch('/api/auth/ping');
      // Auf 401 reagiert der Fetch-Interceptor (handleSessionEvicted) —
      // wir müssen hier nichts weiter tun.
    } catch (err) {
      // Netzwerk-Fehler nicht eskalieren — der nächste ping läuft
      // ohnehin und der User wird auf echte Outage anders aufmerksam.
      console.warn('[apiClient] heartbeat failed:', err.message);
    }
  };
  const id = setInterval(ping, HEARTBEAT_INTERVAL_MS);
  return () => clearInterval(id);
}

// Hilfsfunktion für die „Aktive Geräte"-UI: holt die aktuelle
// Session-Liste über Supabase RLS (User sieht nur eigene Rows).
export async function listDeviceSessions() {
  const { data, error } = await supabase
    .from('device_sessions')
    .select('id, session_id, user_agent, created_at, last_seen_at')
    .order('last_seen_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

// Eine bestimmte Session abmelden (Self-Service in der UI). RLS
// erlaubt DELETE nur auf eigenen Rows. Der zugehörige Browser-Tab
// bekommt beim nächsten API-Call ein 401 vom Interceptor.
export async function revokeDeviceSession(sessionId) {
  const { error } = await supabase
    .from('device_sessions')
    .delete()
    .eq('session_id', sessionId);
  if (error) throw error;
}
